<template>
    <div>
        <v-row>
            <v-col cols="4"></v-col>

            <v-col cols="4" class="text-center">
                <v-chip
                    color="#f78da7"
                    variant="elevated"
                    size="x-large"
                    prepend-icon="mdi:mdi-text-box-multiple"
                    style="text-align: center; margin-bottom: 8px; color: white"
                    elevation="9"
                >
                    Standard Operating Procedures
                </v-chip>

                <v-divider class="border-opacity-25"></v-divider>
            </v-col>

            <v-col cols="4" class="text-end">
                <v-btn
                    v-if="can('create hrForm')"
                    color="primary"
                    @click="showAddFormDialog = true"
                >
                    <font-awesome-icon icon="fa-solid fa-plus" class="pr-3" />
                    Upload Document
                </v-btn>
            </v-col>
        </v-row>

        <v-row>
            <!-- SOP LIST -->
            <v-col cols="12" md="5">
                <v-card elevation="11" class="rounded-lg">
                    <v-list class="sop-list">
                        <template
                            v-for="department in departments"
                            :key="department.id"
                        >
                            <template
                                v-if="getDepartmentSops(department.id).length"
                            >
                                <v-divider class="my-1"></v-divider>

                                <v-list-subheader
                                    style="
                                        font-weight: bold;
                                        font-size: 1.25rem;
                                    "
                                >
                                    {{ department.name }}
                                </v-list-subheader>

                                <v-divider class="my-1"></v-divider>

                                <v-list-item
                                    v-for="sop in getDepartmentSops(
                                        department.id
                                    )"
                                    :key="sop.id"
                                    color="primary"
                                    :class="{
                                        'selected-sop':
                                            selectedSop?.id === sop.id,
                                    }"
                                    @click="selectSop(sop)"
                                >
                                    <template #title>
                                        <div class="sop-title">
                                            {{ sop.title }}
                                        </div>

                                        <div class="sop-upload-date">
                                            <v-icon
                                                icon="mdi:mdi-calendar-outline"
                                                size="13"
                                                class="mr-1"
                                            ></v-icon>
                                            Uploaded
                                            {{ formatDate(sop.created_at) }}
                                        </div>
                                    </template>

                                    <template
                                        #append
                                        v-if="can('delete hrForm')"
                                    >
                                        <v-btn
                                            color="error"
                                            icon="fa-solid fa-trash"
                                            size="x-small"
                                            @click.stop="deleteSop(sop)"
                                        ></v-btn>
                                    </template>
                                </v-list-item>
                            </template>
                        </template>

                        <!-- UNASSIGNED -->
                        <template v-if="unassignedSops.length">
                            <v-divider class="my-1"></v-divider>

                            <v-list-subheader
                                style="font-weight: bold; font-size: 1.25rem"
                            >
                                Unassigned
                            </v-list-subheader>

                            <v-divider class="my-1"></v-divider>

                            <v-list-item
                                v-for="sop in unassignedSops"
                                :key="sop.id"
                                color="primary"
                                :class="{
                                    'selected-sop': selectedSop?.id === sop.id,
                                }"
                                @click="selectSop(sop)"
                            >
                                <template #title>
                                    <div class="sop-title">
                                        {{ sop.title }}
                                    </div>

                                    <div class="sop-upload-date">
                                        <v-icon
                                            icon="mdi:mdi-calendar-outline"
                                            size="13"
                                            class="mr-1"
                                        ></v-icon>
                                        Uploaded
                                        {{ formatDate(sop.created_at) }}
                                    </div>
                                </template>

                                <template #append v-if="can('delete hrForm')">
                                    <v-btn
                                        color="error"
                                        icon="fa-solid fa-trash"
                                        size="x-small"
                                        @click.stop="deleteSop(sop)"
                                    ></v-btn>
                                </template>
                            </v-list-item>
                        </template>

                        <v-list-item v-if="!sops.length">
                            <v-list-item-title class="text-medium-emphasis">
                                No SOP documents have been uploaded.
                            </v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-card>
            </v-col>

            <!-- PREVIEW -->
            <v-col cols="12" md="7">
                <v-card elevation="11" class="rounded-lg h-100">
                    <!-- Nothing selected -->
                    <template v-if="!selectedSop">
                        <div class="preview-placeholder">
                            <v-icon
                                icon="mdi:mdi-file-search-outline"
                                size="64"
                                class="mb-4"
                            ></v-icon>

                            <div class="text-h6">Select an SOP to preview</div>

                            <div class="text-body-2 text-medium-emphasis mt-2">
                                Select a document from the list to view its
                                details.
                            </div>
                        </div>
                    </template>

                    <!-- SOP selected -->
                    <template v-else>
                        <v-card-title class="d-flex align-center">
                            <div class="text-truncate">
                                {{ selectedSop.title }}
                            </div>

                            <v-spacer></v-spacer>

                            <v-btn
                                v-if="canOpenInBrowser(selectedSop)"
                                variant="elevated"
                                color="primary"
                                :href="getSopUrl(selectedSop)"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <v-icon start> mdi:mdi-open-in-new </v-icon>
                                Open
                            </v-btn>
                        </v-card-title>

                        <v-divider></v-divider>

                        <!-- PDF -->
                        <iframe
                            v-if="isPdf(selectedSop)"
                            :src="getSopUrl(selectedSop)"
                            class="pdf-preview"
                        ></iframe>

                        <!-- IMAGE -->
                        <v-img
                            v-else-if="isImage(selectedSop)"
                            :src="getSopUrl(selectedSop)"
                            class="image-preview"
                            contain
                        ></v-img>

                        <!-- EVERYTHING ELSE -->
                        <div v-else class="preview-placeholder">
                            <v-icon
                                icon="mdi:mdi-file-document-outline"
                                size="80"
                                class="mb-4"
                            ></v-icon>

                            <div class="text-h6">
                                {{ getFileTypeLabel(selectedSop) }}
                            </div>

                            <div class="text-body-1 mt-2">
                                {{ selectedSop.original_filename }}
                            </div>
                        </div>

                        <v-divider></v-divider>

                        <!-- METADATA -->
                        <v-card-text>
                            <v-row>
                                <v-col cols="12" sm="6">
                                    <div
                                        class="text-caption text-medium-emphasis"
                                    >
                                        File name
                                    </div>
                                    <div class="text-body-2">
                                        {{ selectedSop.original_filename }}
                                    </div>
                                </v-col>

                                <v-col cols="12" sm="6">
                                    <div
                                        class="text-caption text-medium-emphasis"
                                    >
                                        File type
                                    </div>
                                    <div class="text-body-2">
                                        {{ getFileTypeLabel(selectedSop) }}
                                    </div>
                                </v-col>

                                <v-col cols="12" sm="6">
                                    <div
                                        class="text-caption text-medium-emphasis"
                                    >
                                        File size
                                    </div>
                                    <div class="text-body-2">
                                        {{
                                            formatFileSize(
                                                selectedSop.file_size
                                            )
                                        }}
                                    </div>
                                </v-col>

                                <v-col cols="12" sm="6">
                                    <div
                                        class="text-caption text-medium-emphasis"
                                    >
                                        Uploaded
                                    </div>
                                    <div class="text-body-2">
                                        {{ formatDate(selectedSop.created_at) }}
                                    </div>
                                </v-col>

                                <v-col cols="12">
                                    <div
                                        class="text-caption text-medium-emphasis"
                                    >
                                        Departments
                                    </div>

                                    <div
                                        v-if="selectedSop.departments?.length"
                                        class="d-flex flex-wrap ga-2 mt-1"
                                    >
                                        <v-chip
                                            v-for="department in selectedSop.departments"
                                            :key="department.id"
                                            size="small"
                                            color="primary"
                                        >
                                            {{ department.name }}
                                        </v-chip>
                                    </div>

                                    <div
                                        v-else
                                        class="text-body-2 text-medium-emphasis"
                                    >
                                        Unassigned
                                    </div>
                                </v-col>
                            </v-row>

                            <div class="d-flex justify-end mt-4">
                                <v-btn
                                    variant="elevated"
                                    color="primary"
                                    :href="getSopUrl(selectedSop)"
                                    :download="selectedSop.original_filename"
                                >
                                    <v-icon start> mdi:mdi-download </v-icon>
                                    Download
                                </v-btn>
                            </div>
                        </v-card-text>
                    </template>
                </v-card>
            </v-col>
        </v-row>

        <!-- UPLOAD DIALOG -->
        <v-dialog v-model="showAddFormDialog" max-width="700">
            <v-card title="Add SOP">
                <v-card-text>
                    <form @submit.prevent="uploadSop">
                        <v-text-field
                            label="Title"
                            variant="underlined"
                            v-model="newSop.title"
                            color="primary"
                            :error-messages="errors.title"
                        ></v-text-field>

                        <v-file-input
                            v-model="newSop.file"
                            label="Document"
                            variant="underlined"
                            prepend-icon="mdi:mdi-file-document"
                            :error-messages="errors.file"
                            show-size
                        ></v-file-input>

                        <v-select
                            v-model="newSop.departments"
                            :items="departments"
                            item-title="name"
                            item-value="id"
                            label="Departments"
                            variant="underlined"
                            multiple
                            chips
                            closable-chips
                            clearable
                            color="primary"
                            hint="Leave blank if this SOP does not apply to a specific department."
                            persistent-hint
                            :error-messages="errors.departments"
                        ></v-select>

                        <v-card-actions class="px-0">
                            <v-spacer></v-spacer>

                            <v-btn @click="showAddFormDialog = false">
                                Close
                            </v-btn>

                            <v-btn
                                variant="elevated"
                                color="primary"
                                type="submit"
                                :loading="uploading"
                                :disabled="uploading"
                            >
                                Upload
                            </v-btn>
                        </v-card-actions>
                    </form>

                    <v-alert
                        v-if="showAlert"
                        :type="alert.type"
                        :title="alert.title"
                        :text="alert.text"
                    ></v-alert>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from "axios";
import { Inertia } from "@inertiajs/inertia";

export default {
    data() {
        return {
            // departments: this.$page.props.departments ?? [],
            // sops: this.$page.props.sops ?? [],

            selectedSop: null,

            showAddFormDialog: false,
            uploading: false,

            errors: {
                title: [],
                file: [],
                departments: [],
            },

            newSop: {
                title: "",
                file: null,
                departments: [],
            },

            showAlert: false,
            alert: {
                type: "",
                title: "",
                text: "",
            },
        };
    },

    computed: {
        departments() {
            return this.$page.props.departments ?? [];
        },
        sops() {
            return this.$page.props.sops ?? [];
        },
        unassignedSops() {
            return this.sops.filter((sop) => !sop.departments?.length);
        },
    },

    methods: {
        getDepartmentSops(departmentId) {
            return this.sops.filter((sop) =>
                sop.departments?.some(
                    (department) => department.id === departmentId
                )
            );
        },

        getSopUrl(sop) {
            return `${window.location.origin}/storage/${sop.file_path.replace(
                "public/",
                ""
            )}`;
        },

        uploadSop() {
            if (!this.validateForm()) {
                return;
            }

            this.uploading = true;

            const formData = new FormData();

            formData.append("title", this.newSop.title.trim());

            formData.append("file", this.newSop.file);

            this.newSop.departments.forEach((departmentId) => {
                formData.append("departments[]", departmentId);
            });

            axios
                .post(route("hr.sops.store"), formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                })
                .then(() => {
                    this.showAlert = true;
                    this.alert = {
                        type: "success",
                        title: "SOP Uploaded",
                        text: "The SOP has been uploaded successfully.",
                    };

                    Inertia.reload({
                        only: ["sops", "departments"],
                        onFinish: () => {
                            this.uploading = false;
                            this.showAddFormDialog = false;
                        },
                    });
                })
                .catch((error) => {
                    if (error.response?.status === 422) {
                        this.errors = {
                            title: error.response.data.errors?.title ?? [],
                            file: error.response.data.errors?.file ?? [],
                            departments:
                                error.response.data.errors?.departments ?? [],
                        };
                    }

                    this.showAlert = true;
                    this.alert = {
                        type: "error",
                        title: "Upload Failed",
                        text: "An error occurred while uploading the SOP. Please check the form for errors and try again.",
                    };
                })
                .finally(() => {
                    this.uploading = false;
                });
        },

        validateForm() {
            this.errors = {
                title: [],
                file: [],
                departments: [],
            };

            let valid = true;

            if (!this.newSop.title.trim()) {
                this.errors.title = ["Title is required"];
                valid = false;
            }

            if (!this.newSop.file) {
                this.errors.file = ["File is required"];
                valid = false;
            }

            return valid;
        },

        deleteSop(sop) {
            if(!confirm(`Are you sure you want to delete the SOP "${sop.title}"?\n\nThis action cannot be undone.`)) {
                return;
            }

            axios
                .delete(
                    route("hr.sops.destroy", {
                        sop: sop.id,
                    })
                )
                .then((response) => {
                    Inertia.reload({
                        only: ["sops", "departments"],
                    });

                    if (this.selectedSop?.id === sop.id) {
                        this.selectedSop = null;
                    }
                })
                .catch((error) => {
                    console.log(error.response);

                    alert(
                        "An error occured while trying to delete the SOP. Please try again."
                    );
                });
        },

        selectSop(sop) {
            this.selectedSop = sop;
        },

        isPdf(sop) {
            return sop.mime_type === "application/pdf";
        },

        isImage(sop) {
            return sop.mime_type?.startsWith("image/") ||
                /\.(pdf|png|jpe?g|gif|webp|bmp|svg)$/.test(sop.original_filename || "");
        },

        canOpenInBrowser(sop) {
            return this.isPdf(sop) || this.isImage(sop);
        },

        getFileTypeLabel(sop) {
            const mimeType = (sop.mime_type || "").toLowerCase();
            const filename = (sop.original_filename || "").toLowerCase();

            // Check filename extensions first for reliable identification
            if (/\.(xlsx|xls|csv|ods)$/.test(filename)) {
                return "Excel Spreadsheet";
            }

            if (/\.(doc|docx|odt|rtf)$/.test(filename)) {
                return "Word Document";
            }

            if (/\.(ppt|pptx|odp)$/.test(filename)) {
                return "PowerPoint Presentation";
            }

            if (/\.pdf$/.test(filename) || mimeType === "application/pdf") {
                return "PDF Document";
            }

            if (mimeType.startsWith("image/")) {
                return "Image";
            }

            if (
                mimeType.includes("spreadsheet") ||
                mimeType.includes("excel") ||
                mimeType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
                mimeType === "application/vnd.ms-excel"
            ) {
                return "Excel Spreadsheet";
            }

            if (
                mimeType.includes("word") ||
                mimeType.includes("msword") ||
                mimeType.includes("document")
            ) {
                return "Word Document";
            }

            if (
                mimeType.includes("powerpoint") ||
                mimeType.includes("presentation")
            ) {
                return "PowerPoint Presentation";
            }

            return mimeType || "Document";
        },

        formatFileSize(bytes) {
            if (!bytes) {
                return "Unknown";
            }

            const units = ["Bytes", "KB", "MB", "GB"];

            let size = bytes;
            let unit = 0;

            while (size >= 1024 && unit < units.length - 1) {
                size /= 1024;
                unit++;
            }

            return `${size.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
        },

        formatDate(date) {
            if (!date) {
                return "Unknown";
            }

            return new Date(date).toLocaleDateString();
        },
    },
};
</script>

<style scoped>
.sop-title {
    width: 100%;
    word-wrap: break-word;
    overflow-wrap: break-word;
}

.sop-upload-date {
    display: flex;
    align-items: center;
    margin-top: 3px;
    font-size: 0.75rem;
    color: rgba(var(--v-theme-on-surface), 0.55);
}

.sop-list {
    overflow-x: hidden;
}

:deep(.v-list-item) {
    transition: background-color 0.15s ease, box-shadow 0.15s ease;
}

:deep(.v-list-item:hover) {
    background-color: rgba(var(--v-theme-primary), 0.08);
}

:deep(.v-list-item.selected-sop) {
    background-color: rgba(var(--v-theme-primary), 0.14);
}

.preview-placeholder {
    min-height: 400px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 32px;
}

.pdf-preview {
    display: block;
    width: 100%;
    height: 600px;
    border: none;
}

.image-preview {
    width: 100%;
    height: 600px;
}
</style>
